import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPanelTitle,
  AdminTabButton,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminLinkAction,
  AdminSupportingText,
  AdminAlertText,
  ADMIN_DETAIL_MAIN_HEADER_CLASS,
  ADMIN_DETAIL_TABLE_CLASS,
  adminDetailTabContentClassName,
} from '../shared/adminUx';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { getDetailTablePagination } from '../../../utils/configurationTableUx';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { DEFAULT_DECISION_CHECK_LABELS } from '../../../data/complianceConfiguration';
import { defaultFormInputsForItem } from '../../../data/licensingCredentialRecords';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'submission', label: 'Submission' },
  { id: 'decision', label: 'Decision' },
  { id: 'notes', label: 'Internal Notes' },
  { id: 'logs', label: 'Audit Log' },
];

const FILE_ACTION_GRID = 'minmax(0, 2fr) minmax(72px, auto)';
const SUBMISSION_HISTORY_GRID =
  'minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(0, 1.2fr) minmax(72px, auto)';
const NOTES_TABLE_GRID =
  'minmax(0, 2.2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(72px, auto)';
const AUDIT_LOG_GRID = 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1.8fr) minmax(0, 1.1fr)';

const OPTIONAL_MANDATORY_CHECKS = new Set(['State register checked']);

const APPLIED_RULE_BY_SOURCE_ID = {
  'LIC-0002': 'COM-0001',
  'LIC-0001': 'COM-0002',
  'BDG-0001': 'COM-0003',
  'TKT-0001': 'COM-0004',
};

const appliedRuleIdForSource = (itemId, itemName) => {
  if (APPLIED_RULE_BY_SOURCE_ID[itemId]) return APPLIED_RULE_BY_SOURCE_ID[itemId];
  if (itemName === 'Plumbing Licence') return 'COM-0001';
  if (itemName === 'Electrical Licence' || /electrical/i.test(itemName || '')) return 'COM-0002';
  if (/first aid/i.test(itemName || '')) return 'COM-0003';
  if (itemName === 'White Card') return 'COM-0004';
  return null;
};

const queueTypeToLbtType = (complianceType) => {
  if (complianceType === 'Badge') return 'badge';
  if (complianceType === 'Ticket') return 'trade_ticket';
  return 'licence';
};

const OPTIONAL_SUBMISSION_FIELDS = new Set([
  'Endorsements',
  'Licence Conditions / Restrictions',
  'Licence Variation',
  'Scope of Work',
  'Conditions / Restrictions',
  'Training Provider / RTO',
  'RTO Code',
  'Course / Unit Name',
  'Course / Unit Code',
  'Course / Unit Name(s)',
  'Course / Unit Code(s)',
  'Plant / Equipment Type',
  'Class / Level',
  'Certificate / Reference Number',
  'Check / Reference Number',
  'Cover Details',
  'Cover Amount',
]);

const VALIDITY_SUBMISSION_LABELS = new Set([
  'Issue Date',
  'Expiry Date',
  'Endorsements',
  'Scope of Work',
  'Licence Conditions / Restrictions',
  'Licence Variation',
  'Cover Start Date',
  'Cover End Date',
  'Conditions / Restrictions',
]);

const submissionValueForField = (label, { sourceId, itemName, holderName }) => {
  const isPlumbing = sourceId === 'LIC-0002' || itemName === 'Plumbing Licence';

  if (label === 'Holder Name' || label === 'Policyholder / Insured Name') {
    return holderName || '—';
  }
  if (!isPlumbing) return '—';

  switch (label) {
    case 'Licence Number':
      return 'LIC-884201';
    case 'Issuing Authority':
    case 'Issuing Organisation':
      return 'Victorian Building Authority';
    case 'Jurisdiction':
      return 'Victoria';
    case 'Licence Class':
      return 'Plumbing Contractor';
    case 'Licence Category':
      return 'Contractor';
    case 'Issue Date':
      return formatDate('2025-03-12');
    case 'Expiry Date':
      return formatDate('2026-03-12');
    case 'Scope of Work':
      return 'Plumbing work';
    case 'Endorsements':
    case 'Licence Conditions / Restrictions':
    case 'Licence Variation':
      return '—';
    default:
      return OPTIONAL_SUBMISSION_FIELDS.has(label) ? '—' : '—';
  }
};

const buildSubmissionFields = ({ complianceType, complianceItem, sourceId, holderName }) => {
  const inputs = defaultFormInputsForItem(
    complianceItem,
    queueTypeToLbtType(complianceType)
  );
  const fields = inputs
    .filter((input) => input.fieldControl !== 'File upload')
    .map((input) => ({
      label: input.fieldLabel,
      value: submissionValueForField(input.fieldLabel, {
        sourceId,
        itemName: complianceItem,
        holderName,
      }),
    }));
  return {
    identityFields: fields.filter((field) => !VALIDITY_SUBMISSION_LABELS.has(field.label)),
    validityFields: fields.filter((field) => VALIDITY_SUBMISSION_LABELS.has(field.label)),
  };
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart}, ${timePart}`;
};

const decisionIdFromCaseId = (caseId) => {
  const digits = String(caseId || '').replace(/\D/g, '');
  return `CMP-DEC-${(digits || '0000').slice(-4)}`;
};

const recordIdFromCaseId = (caseId) => {
  const digits = String(caseId || '').replace(/\D/g, '');
  return `CR-${(digits || '0000').padStart(6, '0').slice(-6)}`;
};

const normalizeCaseType = (raw) => {
  if (!raw) return 'New';
  if (raw === 'New Submission') return 'New';
  return raw;
};

const relatedRecordPath = (value, previousCaseId) => {
  if (!value || value === '—') return null;
  const id = String(value);
  if (id.startsWith('ST-')) return `/admin/support/tickets/${encodeURIComponent(id)}`;
  if (id.startsWith('APL-') || id.startsWith('APP-')) {
    return `/admin/appeals/${encodeURIComponent(id)}`;
  }
  if (id.startsWith('CMP-DEC')) {
    return previousCaseId
      ? `/admin/verification/archive/${encodeURIComponent(previousCaseId)}`
      : null;
  }
  if (id.startsWith('CMP-')) return `/admin/verification/archive/${encodeURIComponent(id)}`;
  return null;
};

const DetailField = ({ label, value }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue>
      {value != null && value !== '' ? value : '—'}
    </AdminDetailFieldValue>
  </div>
);

const LinkedIdField = ({ label, value, onOpen }) => (
  <div className="space-y-1">
    <AdminDetailFieldLabel className="mb-1">{label}</AdminDetailFieldLabel>
    {value ? (
      <AdminDetailFieldValue>
        {onOpen ? (
          <AdminLinkAction type="button" onClick={() => onOpen(value)}>
            {value}
          </AdminLinkAction>
        ) : (
          value
        )}
      </AdminDetailFieldValue>
    ) : (
      <AdminDetailFieldValue>—</AdminDetailFieldValue>
    )}
  </div>
);

const buildReviewChecks = ({ completed = true, rejected = false } = {}) =>
  DEFAULT_DECISION_CHECK_LABELS.map((label, index) => {
    const mandatory = !OPTIONAL_MANDATORY_CHECKS.has(label);
    let checked = completed && mandatory;
    if (rejected && (label === 'Submitted details match evidence' || label === 'Number confirmed')) {
      checked = false;
    }
    return {
      id: `check-${index + 1}`,
      label,
      mandatory,
      checked,
    };
  });

const buildApprovedRenewalDetail = (complianceCaseId) => {
  const id = complianceCaseId || 'CMP-004180';
  const submittedAt = '2026-03-03T09:00:00+10:00';
  const decidedOn = '2026-03-15T14:18:00+10:00';
  const finalisedOn = '2026-03-15T14:20:00+10:00';
  const handedOffOn = '2026-03-15T14:21:00+10:00';
  const fields = buildSubmissionFields({
    complianceType: 'Licence',
    complianceItem: 'Plumbing Licence',
    sourceId: 'LIC-0002',
    holderName: 'Mark Taylor',
  });
  return {
    complianceCaseId: id,
    complianceDecisionId: 'CMP-DEC-4180',
    complianceItem: 'Plumbing Licence',
    caseType: 'Renewal',
    caseStatus: 'Decided',
    supportTeamMember: 'Jordan Lee',
    submittedAt,
    finalisedBy: 'Jordan Lee',
    finalisedOn,
    snapshot: {
      appliedRuleId: 'COM-0001',
      sourceId: 'LIC-0002',
      complianceType: 'Licence',
      complianceItem: 'Plumbing Licence',
      tradeCategory: 'Plumbing',
    },
    businessName: 'Apex Plumbing',
    businessId: 'BUS-001284',
    abn: '12 345 678 901',
    accountHolderLegalName: 'Mark Taylor',
    businessTradeCategory: 'Plumbing',
    accountStatus: 'Active',
    recordOutcome: {
      action: 'Replace Record',
      recordId: 'CR-004180',
      recordStatus: 'Active',
      effectiveFrom: '2026-03-15',
      expiryDate: '2027-03-14',
      previousRecord: 'CR-003981',
    },
    linkedCases: {
      previousComplianceCase: 'CMP-003981',
      previousComplianceDecision: 'CMP-DEC-3981',
      relatedAppeal: null,
      relatedSupportTicket: null,
    },
    submission: {
      submittedBy: 'Apex Plumbing',
      submittedAt,
      latestSubmission: 'Initial Submission',
      submissionCount: 1,
      identityFields: fields.identityFields,
      validityFields: fields.validityFields,
    },
    submittedForm: { name: 'plumbing_licence_form.pdf' },
    supportingFiles: [
      { name: 'plumbing_licence_front.pdf' },
      { name: 'plumbing_licence_back.jpg' },
    ],
    submissionHistory: [
      {
        name: 'Plumbing Licence',
        submittedBy: 'Apex Plumbing',
        submittedOn: submittedAt,
        status: 'Final',
      },
    ],
    reviewChecks: buildReviewChecks({ completed: true }),
    finalDecision: {
      outcome: 'Approved',
      decidedBy: 'Jordan Lee',
      decidedOn,
      rejectionBasis: [],
      rejectionNotes: '',
    },
    informationRequest: null,
    finalisation: [
      {
        item: 'Business Record Action',
        result: 'Replace Record',
        relatedRecord: 'CR-004180',
        completedOn: finalisedOn,
      },
      {
        item: 'Archive Handoff',
        result: 'Handed off',
        relatedRecord: id,
        completedOn: handedOffOn,
      },
    ],
    internalNotes: [
      {
        id: 'NOTE-1',
        addedBy: 'Jordan Lee',
        addedOn: '2026-03-15T14:16:00+10:00',
        text: 'Evidence confirmed against Victorian Building Authority records.',
        additions: [],
      },
    ],
    auditLog: [
      {
        id: 'LOG-001',
        dateTime: submittedAt,
        event: 'Case received',
        details: `System created ${id} as New.`,
        relatedRecord: '—',
      },
      {
        id: 'LOG-002',
        dateTime: submittedAt,
        event: 'Assigned',
        details: 'System assigned Jordan Lee.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-003',
        dateTime: '2026-03-03T09:12:00+10:00',
        event: 'Start Case',
        details: 'Jordan Lee started the case. Case status Under Review.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-004',
        dateTime: decidedOn,
        event: 'Final decision',
        details: 'Jordan Lee recorded Approved (CMP-DEC-4180).',
        relatedRecord: 'CMP-DEC-4180',
      },
      {
        id: 'LOG-005',
        dateTime: finalisedOn,
        event: 'Completion result',
        details: 'Business record action Replace Record succeeded.',
        relatedRecord: 'CR-004180',
      },
      {
        id: 'LOG-006',
        dateTime: handedOffOn,
        event: 'Archive handoff',
        details: `System handed ${id} to Compliance Archive.`,
        relatedRecord: id,
      },
    ],
  };
};

const buildRejectedNewDetail = (complianceCaseId) => {
  const id = complianceCaseId || 'CMP-004185';
  const decisionId = decisionIdFromCaseId(id);
  const submittedAt = '2026-03-10T09:18:00+10:00';
  const decidedOn = '2026-03-18T11:52:00+10:00';
  const finalisedOn = '2026-03-18T12:00:00+10:00';
  const handedOffOn = '2026-03-18T12:01:00+10:00';
  return {
    ...buildApprovedRenewalDetail(id),
    complianceCaseId: id,
    complianceDecisionId: decisionId,
    complianceItem: 'White Card',
    caseType: 'New',
    supportTeamMember: 'Mia Chen',
    submittedAt,
    finalisedBy: 'Mia Chen',
    finalisedOn,
    snapshot: {
      appliedRuleId: 'COM-0004',
      sourceId: 'TKT-0001',
      complianceType: 'Ticket',
      complianceItem: 'White Card',
      tradeCategory: 'All Trades',
    },
    businessName: 'Bright Spark Electrical',
    businessId: 'BUS-001285',
    abn: null,
    accountHolderLegalName: null,
    businessTradeCategory: 'Electrical',
    accountStatus: 'Active',
    recordOutcome: {
      action: 'No Record',
      recordId: null,
      recordStatus: '—',
      effectiveFrom: null,
      expiryDate: null,
      previousRecord: null,
    },
    linkedCases: {
      previousComplianceCase: null,
      previousComplianceDecision: null,
      relatedAppeal: null,
      relatedSupportTicket: null,
    },
    submission: {
      submittedBy: 'Bright Spark Electrical',
      submittedAt,
      latestSubmission: 'Initial Submission',
      submissionCount: 1,
      ...buildSubmissionFields({
        complianceType: 'Ticket',
        complianceItem: 'White Card',
        sourceId: 'TKT-0001',
        holderName: null,
      }),
    },
    submittedForm: { name: 'white_card_form.pdf' },
    supportingFiles: [{ name: 'white_card_front.pdf' }],
    submissionHistory: [
      {
        name: 'White Card',
        submittedBy: 'Bright Spark Electrical',
        submittedOn: submittedAt,
        status: 'Submitted',
      },
    ],
    reviewChecks: buildReviewChecks({ completed: false, rejected: true }),
    finalDecision: {
      outcome: 'Rejected',
      decidedBy: 'Mia Chen',
      decidedOn,
      rejectionBasis: ['Information Does Not Match', 'Not Confirmed by Issuing Body'],
      rejectionNotes: 'Certificate details could not be confirmed against the issuing body record.',
    },
    informationRequest: null,
    finalisation: [
      {
        item: 'Business Record Action',
        result: 'No Record',
        relatedRecord: '—',
        completedOn: finalisedOn,
      },
      {
        item: 'Archive Handoff',
        result: 'Handed off',
        relatedRecord: id,
        completedOn: handedOffOn,
      },
    ],
    internalNotes: [],
    auditLog: [
      {
        id: 'LOG-R1',
        dateTime: submittedAt,
        event: 'Case received',
        details: `System created ${id} as New.`,
        relatedRecord: '—',
      },
      {
        id: 'LOG-R2',
        dateTime: submittedAt,
        event: 'Assigned',
        details: 'System assigned Mia Chen.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-R3',
        dateTime: '2026-03-10T10:02:00+10:00',
        event: 'Start Case',
        details: 'Mia Chen started the case. Case status Under Review.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-R4',
        dateTime: decidedOn,
        event: 'Final decision',
        details: 'Mia Chen recorded Rejected (CMP-DEC-4181). Rejection Basis: Information Does Not Match, Not Confirmed by Issuing Body.',
        relatedRecord: 'CMP-DEC-4181',
      },
      {
        id: 'LOG-R5',
        dateTime: finalisedOn,
        event: 'Completion result',
        details: 'Business record action No Record succeeded.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-R6',
        dateTime: handedOffOn,
        event: 'Archive handoff',
        details: `System handed ${id} to Compliance Archive.`,
        relatedRecord: id,
      },
    ],
  };
};

const buildReplacementApprovedDetail = (complianceCaseId) => {
  const id = complianceCaseId || 'CMP-004182';
  const submittedAt = '2026-03-12T14:35:00+10:00';
  const requestAt = '2026-03-13T09:10:00+10:00';
  const decidedOn = '2026-03-20T16:22:00+10:00';
  const finalisedOn = '2026-03-20T16:30:00+10:00';
  const handedOffOn = '2026-03-20T16:31:00+10:00';
  return {
    ...buildApprovedRenewalDetail(id),
    complianceCaseId: id,
    complianceDecisionId: 'CMP-DEC-4182',
    complianceItem: 'Electrical Licence',
    caseType: 'Replacement',
    supportTeamMember: 'Jordan Lee',
    submittedAt,
    finalisedBy: 'Jordan Lee',
    finalisedOn,
    snapshot: {
      appliedRuleId: 'COM-0002',
      sourceId: 'LIC-0001',
      complianceType: 'Licence',
      complianceItem: 'Electrical Licence',
      tradeCategory: 'Electrical',
    },
    businessName: 'Metro Building Group',
    businessId: 'BUS-001287',
    abn: null,
    accountHolderLegalName: null,
    businessTradeCategory: 'Building',
    recordOutcome: {
      action: 'Replace Record',
      recordId: 'CR-004182',
      recordStatus: 'Active',
      effectiveFrom: '2026-03-20',
      expiryDate: '2027-03-20',
      previousRecord: 'CR-003850',
    },
    linkedCases: {
      previousComplianceCase: 'CMP-003850',
      previousComplianceDecision: 'CMP-DEC-3850',
      relatedAppeal: null,
      relatedSupportTicket: 'ST-004822',
    },
    submission: {
      submittedBy: 'Metro Building Group',
      submittedAt,
      latestSubmission: 'Initial Submission',
      submissionCount: 1,
      ...buildSubmissionFields({
        complianceType: 'Licence',
        complianceItem: 'Electrical Licence',
        sourceId: 'LIC-0001',
        holderName: null,
      }),
    },
    submittedForm: { name: 'electrical_licence_form.pdf' },
    supportingFiles: [{ name: 'electrical_licence_front.pdf' }],
    submissionHistory: [
      {
        name: 'Electrical Licence',
        submittedBy: 'Metro Building Group',
        submittedOn: submittedAt,
        status: 'Final',
      },
    ],
    informationRequest: {
      content: 'Please provide a clearer copy of the registration certificate showing the current class.',
      responsePeriodDays: 14,
      availableAt: requestAt,
      dueAt: '2026-03-27T09:10:00+10:00',
      timeZone: 'Australia/Melbourne',
      relatedSupportTicket: 'ST-004822',
    },
    finalDecision: {
      outcome: 'Approved',
      decidedBy: 'Jordan Lee',
      decidedOn,
      rejectionBasis: [],
      rejectionNotes: '',
    },
    finalisation: [
      {
        item: 'Business Record Action',
        result: 'Replace Record',
        relatedRecord: 'CR-004182',
        completedOn: finalisedOn,
      },
      {
        item: 'Archive Handoff',
        result: 'Handed off',
        relatedRecord: id,
        completedOn: handedOffOn,
      },
    ],
    internalNotes: [
      {
        id: 'NOTE-1',
        addedBy: 'Jordan Lee',
        addedOn: requestAt,
        text: 'Asked for a clearer certificate copy before completing replacement.',
        additions: [
          {
            text: 'Clearer copy received through the linked Support Ticket. Class confirmed.',
            addedBy: 'Jordan Lee',
            addedOn: '2026-03-19T11:04:00+10:00',
          },
        ],
      },
    ],
    auditLog: [
      {
        id: 'LOG-P1',
        dateTime: submittedAt,
        event: 'Case received',
        details: `System created ${id} as New.`,
        relatedRecord: '—',
      },
      {
        id: 'LOG-P2',
        dateTime: submittedAt,
        event: 'Assigned',
        details: 'System assigned Jordan Lee.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-P3',
        dateTime: '2026-03-12T15:02:00+10:00',
        event: 'Start Case',
        details: 'Jordan Lee started the case. Case status Under Review.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-P4',
        dateTime: requestAt,
        event: 'Information requested',
        details: 'Jordan Lee requested more information. Linked Support Ticket ST-004822. Due 27 Mar 2026, 9:10 am.',
        relatedRecord: 'ST-004822',
      },
      {
        id: 'LOG-P5',
        dateTime: decidedOn,
        event: 'Final decision',
        details: 'Jordan Lee recorded Approved (CMP-DEC-4182).',
        relatedRecord: 'CMP-DEC-4182',
      },
      {
        id: 'LOG-P6',
        dateTime: finalisedOn,
        event: 'Completion result',
        details: 'Business record action Replace Record succeeded.',
        relatedRecord: 'CR-004182',
      },
      {
        id: 'LOG-P7',
        dateTime: handedOffOn,
        event: 'Archive handoff',
        details: `System handed ${id} to Compliance Archive.`,
        relatedRecord: id,
      },
    ],
  };
};

const buildApprovedBadgeDetail = (complianceCaseId) => {
  const id = complianceCaseId || 'CMP-003983';
  const submittedAt = '2025-03-28T09:00:00+10:00';
  const decidedOn = '2025-04-02T10:18:00+10:00';
  const finalisedOn = '2025-04-02T10:24:00+10:00';
  const handedOffOn = '2025-04-02T10:25:00+10:00';
  return {
    ...buildApprovedRenewalDetail(id),
    complianceCaseId: id,
    complianceDecisionId: 'CMP-DEC-3983',
    complianceItem: 'First Aid',
    caseType: 'New',
    supportTeamMember: 'Jordan Lee',
    submittedAt,
    finalisedBy: 'Jordan Lee',
    finalisedOn,
    snapshot: {
      appliedRuleId: 'COM-0003',
      sourceId: 'BDG-0001',
      complianceType: 'Badge',
      complianceItem: 'First Aid',
      tradeCategory: 'All Trades',
    },
    businessName: 'Urban Gas Works',
    businessId: 'BUS-001286',
    abn: null,
    accountHolderLegalName: null,
    businessTradeCategory: 'Plumbing',
    recordOutcome: {
      action: 'Create Record',
      recordId: 'CR-003983',
      recordStatus: 'Active',
      effectiveFrom: '2025-04-02',
      expiryDate: '2026-04-02',
      previousRecord: null,
    },
    linkedCases: {
      previousComplianceCase: null,
      previousComplianceDecision: null,
      relatedAppeal: null,
      relatedSupportTicket: null,
    },
    submission: {
      submittedBy: 'Urban Gas Works',
      submittedAt,
      latestSubmission: 'Initial Submission',
      submissionCount: 1,
      ...buildSubmissionFields({
        complianceType: 'Badge',
        complianceItem: 'First Aid',
        sourceId: 'BDG-0001',
        holderName: null,
      }),
    },
    submittedForm: { name: 'first_aid_form.pdf' },
    supportingFiles: [{ name: 'first_aid_certificate.pdf' }],
    submissionHistory: [
      {
        name: 'First Aid',
        submittedBy: 'Urban Gas Works',
        submittedOn: submittedAt,
        status: 'Final',
      },
    ],
    finalDecision: {
      outcome: 'Approved',
      decidedBy: 'Jordan Lee',
      decidedOn,
      rejectionBasis: [],
      rejectionNotes: '',
    },
    informationRequest: null,
    finalisation: [
      {
        item: 'Business Record Action',
        result: 'Create Record',
        relatedRecord: 'CR-003983',
        completedOn: finalisedOn,
      },
      {
        item: 'Archive Handoff',
        result: 'Handed off',
        relatedRecord: id,
        completedOn: handedOffOn,
      },
    ],
    internalNotes: [
      {
        id: 'NOTE-1',
        addedBy: 'Jordan Lee',
        addedOn: decidedOn,
        text: 'Certificate matched the holder name on the Business account.',
        additions: [],
      },
    ],
    auditLog: [
      {
        id: 'LOG-B1',
        dateTime: submittedAt,
        event: 'Case received',
        details: `System created ${id} as New.`,
        relatedRecord: '—',
      },
      {
        id: 'LOG-B2',
        dateTime: submittedAt,
        event: 'Assigned',
        details: 'System assigned Jordan Lee.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-B3',
        dateTime: '2025-03-28T09:40:00+10:00',
        event: 'Start Case',
        details: 'Jordan Lee started the case. Case status Under Review.',
        relatedRecord: '—',
      },
      {
        id: 'LOG-B4',
        dateTime: decidedOn,
        event: 'Final decision',
        details: 'Jordan Lee recorded Approved (CMP-DEC-3983).',
        relatedRecord: 'CMP-DEC-3983',
      },
      {
        id: 'LOG-B5',
        dateTime: finalisedOn,
        event: 'Completion result',
        details: 'Business record action Create Record succeeded.',
        relatedRecord: 'CR-003983',
      },
      {
        id: 'LOG-B6',
        dateTime: handedOffOn,
        event: 'Archive handoff',
        details: `System handed ${id} to Compliance Archive.`,
        relatedRecord: id,
      },
    ],
  };
};

const buildGenericArchiveDetail = (id, fromState = {}) => {
  const caseType = normalizeCaseType(fromState.caseType);
  const outcome = fromState.finalOutcome || fromState.finalDecision || 'Approved';
  const rejected = outcome === 'Rejected';
  const keepCurrent = rejected && fromState.recordStatus === 'Active';
  const submittedAt = fromState.submittedAt || fromState.submitted || '2026-03-04T09:00:00+10:00';
  const finalisedOn = fromState.finalisedOn || fromState.finalised || '2026-03-15T11:00:00+10:00';
  const decidedOn = fromState.decidedOn || finalisedOn;
  const member = fromState.supportTeamMember || 'Jordan Lee';
  const decisionId = fromState.decisionId || decisionIdFromCaseId(id);
  const complianceType = fromState.complianceType || fromState.category || 'Licence';
  const complianceItem = fromState.complianceItem || fromState.documentName || 'Plumbing Licence';
  const sourceId = fromState.complianceItemId || fromState.sourceId || 'LIC-0002';
  const businessName = fromState.businessName || 'Archived Business';
  const action = rejected
    ? keepCurrent
      ? 'Keep Current Record'
      : 'No Record'
    : caseType === 'New'
      ? 'Create Record'
      : 'Replace Record';
  const recordId = rejected && !keepCurrent ? null : fromState.recordId || recordIdFromCaseId(id);
  const recordStatus =
    fromState.recordStatus === '—' || fromState.recordStatus === 'No Record'
      ? '—'
      : fromState.recordStatus || (rejected && !keepCurrent ? '—' : 'Active');
  const submissionFields = buildSubmissionFields({
    complianceType,
    complianceItem,
    sourceId,
    holderName: fromState.accountHolderLegalName || null,
  });

  return {
    complianceCaseId: id,
    complianceDecisionId: decisionId,
    complianceItem,
    caseType,
    caseStatus: 'Decided',
    supportTeamMember: member,
    submittedAt,
    finalisedBy: member,
    finalisedOn,
    snapshot: {
      appliedRuleId:
        fromState.appliedRuleId ||
        fromState.configurationId ||
        appliedRuleIdForSource(sourceId, complianceItem),
      sourceId,
      complianceType,
      complianceItem,
      tradeCategory: fromState.tradeCategory ||
        (complianceItem === 'Electrical Licence'
          ? 'Electrical'
          : complianceType === 'Licence'
            ? 'Plumbing'
            : 'All Trades'),
    },
    businessName,
    businessId: fromState.businessId || 'BUS-000000',
    abn: fromState.abn || null,
    accountHolderLegalName: fromState.accountHolderLegalName || null,
    businessTradeCategory: fromState.businessTradeCategory || fromState.tradeCategory || 'Plumbing',
    accountStatus: fromState.accountStatus || 'Active',
    recordOutcome: {
      action,
      recordId,
      recordStatus,
      effectiveFrom: rejected && !keepCurrent ? null : fromState.effectiveFrom || finalisedOn,
      expiryDate: rejected && !keepCurrent ? null : fromState.expiryDate || null,
      previousRecord: caseType === 'New' || (rejected && !keepCurrent) ? null : 'CR-003981',
    },
    linkedCases: {
      previousComplianceCase: caseType === 'New' ? null : 'CMP-003981',
      previousComplianceDecision: caseType === 'New' ? null : 'CMP-DEC-3981',
      relatedAppeal: null,
      relatedSupportTicket: null,
    },
    submission: {
      submittedBy: businessName,
      submittedAt,
      latestSubmission: 'Initial Submission',
      submissionCount: 1,
      ...submissionFields,
    },
    submittedForm: {
      name: `${String(complianceItem).toLowerCase().replace(/\s+/g, '_')}_form.pdf`,
    },
    supportingFiles: [
      { name: `${String(complianceItem).toLowerCase().replace(/\s+/g, '_')}_front.pdf` },
    ],
    submissionHistory: [
      {
        name: complianceItem,
        submittedBy: businessName,
        submittedOn: submittedAt,
        status: 'Final',
      },
    ],
    reviewChecks: buildReviewChecks({ completed: !rejected, rejected }),
    finalDecision: {
      outcome,
      decidedBy: member,
      decidedOn,
      rejectionBasis: rejected ? ['Decision Requirements Not Met'] : [],
      rejectionNotes: rejected ? 'Recorded rejection notes for this case.' : '',
    },
    informationRequest: null,
    finalisation: [
      {
        item: 'Business Record Action',
        result: action,
        relatedRecord: recordId || '—',
        completedOn: finalisedOn,
      },
      {
        item: 'Archive Handoff',
        result: 'Handed off',
        relatedRecord: id,
        completedOn: finalisedOn,
      },
    ],
    internalNotes: rejected
      ? []
      : [
          {
            id: 'NOTE-1',
            addedBy: member,
            addedOn: decidedOn,
            text: 'Review completed before archive handoff.',
            additions: [],
          },
        ],
    auditLog: [
      {
        id: 'LOG-G1',
        dateTime: submittedAt,
        event: 'Case received',
        details: `System created ${id} as New.`,
        relatedRecord: '—',
      },
      {
        id: 'LOG-G2',
        dateTime: submittedAt,
        event: 'Assigned',
        details: `System assigned ${member}.`,
        relatedRecord: '—',
      },
      {
        id: 'LOG-G3',
        dateTime: decidedOn,
        event: 'Final decision',
        details: `${member} recorded ${outcome} (${decisionId}).`,
        relatedRecord: decisionId,
      },
      {
        id: 'LOG-G4',
        dateTime: finalisedOn,
        event: 'Archive handoff',
        details: `System handed ${id} to Compliance Archive.`,
        relatedRecord: id,
      },
    ],
  };
};

const buildDummyArchiveDetail = (complianceCaseId, fromState) => {
  const id = complianceCaseId || fromState?.complianceCaseId || 'CMP-004180';
  if (id === 'CMP-004180') return buildApprovedRenewalDetail(id);
  if (id === 'CMP-004181' || id === 'CMP-004185') return buildRejectedNewDetail(id);
  if (id === 'CMP-004182') return buildReplacementApprovedDetail(id);
  if (id === 'CMP-003983') return buildApprovedBadgeDetail(id);
  return buildGenericArchiveDetail(id, fromState || {});
};

const ComplianceArchiveDetail = () => {
  const { documentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [auditLogCurrentPage, setAuditLogCurrentPage] = useState(1);
  const [archiveItem, setArchiveItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewingNote, setViewingNote] = useState(null);

  const complianceArchiveAuditRows = useMemo(() => {
    if (!archiveItem?.auditLog) return [];
    return archiveItem.auditLog
      .slice()
      .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));
  }, [archiveItem]);

  const auditLogsPagination = getDetailTablePagination(
    complianceArchiveAuditRows.length,
    auditLogCurrentPage
  );
  const paginatedAuditRows = auditLogsPagination.paginate(complianceArchiveAuditRows);

  useEffect(() => {
    setAuditLogCurrentPage(1);
  }, [documentId, activeTab]);

  useEffect(() => {
    const loadDetail = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 120));
      const fromState = location.state?.archiveItem || null;
      setArchiveItem(buildDummyArchiveDetail(documentId, fromState));
      setLoading(false);
    };
    loadDetail();
  }, [documentId, location.state]);

  const openLinked = (id, previousCaseId) => {
    const path = relatedRecordPath(id, previousCaseId);
    if (path) navigate(path);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading archived case...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (!archiveItem) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminAlertText>Archived compliance case not found</AdminAlertText>
        </div>
      </AdminLayout>
    );
  }

  const notes = archiveItem.internalNotes || [];

  return (
    <AdminLayout>
      <div className="tp-container-main">
        <div>
          <DashboardMainContainer className={ADMIN_DETAIL_MAIN_HEADER_CLASS}>
            <AdminPanelTitle className="text-offblack">
              {archiveItem.complianceItem}
            </AdminPanelTitle>
          </DashboardMainContainer>

          <DashboardMainContainer className="!bg-white">
            <div className="border-b border-lightgray">
              <div className="flex space-x-6 overflow-x-auto">
                {TABS.map((tab) => (
                  <AdminTabButton
                    key={tab.id}
                    active={activeTab === tab.id}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </AdminTabButton>
                ))}
              </div>
            </div>

            <div className={adminDetailTabContentClassName(activeTab)}>
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6">
                  <div className="space-y-4">
                    <DetailField label="Compliance Case ID" value={archiveItem.complianceCaseId} />
                    <DetailField
                      label="Compliance Decision ID"
                      value={archiveItem.complianceDecisionId}
                    />
                    <DetailField label="Case Type" value={archiveItem.caseType} />
                    <DetailField label="Case Status" value={archiveItem.caseStatus} />
                    <DetailField label="Support Team Member" value={archiveItem.supportTeamMember} />
                    <DetailField label="Submitted" value={formatDateTime(archiveItem.submittedAt)} />
                    <DetailField label="Finalised By" value={archiveItem.finalisedBy} />
                    <DetailField label="Finalised On" value={formatDateTime(archiveItem.finalisedOn)} />
                  </div>

                  <div className="space-y-4">
                    <DetailField label="Business Name" value={archiveItem.businessName} />
                    <DetailField label="Business ID" value={archiveItem.businessId} />
                    <DetailField label="ABN" value={archiveItem.abn} />
                    <DetailField
                      label="Account Holder Legal Name"
                      value={archiveItem.accountHolderLegalName}
                    />
                    <DetailField label="Trade Category" value={archiveItem.businessTradeCategory} />
                    <DetailField label="Account Status" value={archiveItem.accountStatus} />
                  </div>

                  <div className="space-y-4">
                    <DetailField
                      label="Applied Compliance Rule ID"
                      value={archiveItem.snapshot.appliedRuleId}
                    />
                    <DetailField label="Source ID" value={archiveItem.snapshot.sourceId} />
                    <DetailField label="Type" value={archiveItem.snapshot.complianceType} />
                    <DetailField label="Name" value={archiveItem.snapshot.complianceItem} />
                    <DetailField label="Trade Category" value={archiveItem.snapshot.tradeCategory} />
                  </div>

                  <div className="space-y-4">
                    <DetailField
                      label="Business Record Action"
                      value={archiveItem.recordOutcome?.action}
                    />
                    <DetailField
                      label="Business Compliance Record ID"
                      value={archiveItem.recordOutcome?.recordId}
                    />
                    <DetailField
                      label="Record Status"
                      value={
                        archiveItem.recordOutcome?.recordStatus === 'No Record'
                          ? '—'
                          : archiveItem.recordOutcome?.recordStatus
                      }
                    />
                    <DetailField
                      label="Effective From"
                      value={
                        archiveItem.recordOutcome?.effectiveFrom
                          ? formatDate(archiveItem.recordOutcome.effectiveFrom)
                          : null
                      }
                    />
                    <DetailField
                      label="Expiry Date"
                      value={
                        archiveItem.recordOutcome?.expiryDate
                          ? formatDate(archiveItem.recordOutcome.expiryDate)
                          : null
                      }
                    />
                    <DetailField
                      label="Previous Record"
                      value={archiveItem.recordOutcome?.previousRecord}
                    />
                    <LinkedIdField
                      label="Previous Compliance Case ID"
                      value={archiveItem.linkedCases?.previousComplianceCase}
                      onOpen={(id) => openLinked(id)}
                    />
                    <LinkedIdField
                      label="Previous Compliance Decision ID"
                      value={archiveItem.linkedCases?.previousComplianceDecision}
                      onOpen={
                        archiveItem.linkedCases?.previousComplianceCase
                          ? (id) =>
                              openLinked(id, archiveItem.linkedCases.previousComplianceCase)
                          : undefined
                      }
                    />
                    <LinkedIdField
                      label="Related Appeal ID"
                      value={archiveItem.linkedCases?.relatedAppeal}
                      onOpen={(id) => openLinked(id)}
                    />
                    <LinkedIdField
                      label="Related Support Ticket ID"
                      value={archiveItem.linkedCases?.relatedSupportTicket}
                      onOpen={(id) => openLinked(id)}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'submission' && (
                <div className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
                    <div className="space-y-4">
                      <DetailField
                        label="Submitted By"
                        value={archiveItem.submission.submittedBy}
                      />
                      <DetailField
                        label="Submitted On"
                        value={formatDateTime(archiveItem.submission.submittedAt)}
                      />
                      <DetailField
                        label="Latest Submission"
                        value={archiveItem.submission.latestSubmission}
                      />
                      <DetailField
                        label="Submission Count"
                        value={String(archiveItem.submission.submissionCount ?? 1)}
                      />
                    </div>
                    <div className="space-y-4">
                      {(archiveItem.submission.identityFields || []).map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} />
                      ))}
                    </div>
                    <div className="space-y-4">
                      {(archiveItem.submission.validityFields || []).map((field) => (
                        <DetailField key={field.label} label={field.label} value={field.value} />
                      ))}
                    </div>
                  </div>

                  {archiveItem.submittedForm ? (
                    <div>
                      <AdminPanelTitle className="mb-4">Submitted Form</AdminPanelTitle>
                      <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                        <div
                          className="grid py-3 px-4 text-offblack border-b border-lightgray gap-4"
                          style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                        >
                          <AdminTableHeader>File</AdminTableHeader>
                          <div className="flex items-center justify-center">
                            <AdminTableHeader>Action</AdminTableHeader>
                          </div>
                        </div>
                        <div
                          className="grid py-3 px-4 text-offblack gap-4 items-center"
                          style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                        >
                          <AdminTableBody className="truncate" title={archiveItem.submittedForm.name}>
                            {archiveItem.submittedForm.name}
                          </AdminTableBody>
                          <div className="flex justify-center">
                            <AdminLinkAction
                              type="button"
                              onClick={() => alert(`View: ${archiveItem.submittedForm.name}`)}
                            >
                              View
                            </AdminLinkAction>
                          </div>
                        </div>
                      </DashboardMainContainer>
                    </div>
                  ) : null}

                  <div>
                    <AdminPanelTitle className="mb-4">Supporting Files</AdminPanelTitle>
                    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                      <div
                        className="grid py-3 px-4 text-offblack border-b border-lightgray gap-4"
                        style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                      >
                        <AdminTableHeader>File</AdminTableHeader>
                        <div className="flex items-center justify-center">
                          <AdminTableHeader>Action</AdminTableHeader>
                        </div>
                      </div>
                      {(archiveItem.supportingFiles || []).map((file) => (
                        <div
                          key={file.name}
                          className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center"
                          style={{ gridTemplateColumns: FILE_ACTION_GRID }}
                        >
                          <AdminTableBody className="truncate" title={file.name}>
                            {file.name}
                          </AdminTableBody>
                          <div className="flex justify-center">
                            <AdminLinkAction
                              type="button"
                              onClick={() => alert(`View: ${file.name}`)}
                            >
                              View
                            </AdminLinkAction>
                          </div>
                        </div>
                      ))}
                    </DashboardMainContainer>
                  </div>

                  <div>
                    <AdminPanelTitle className="mb-4">Submission History</AdminPanelTitle>
                    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                      <div
                        className="grid py-3 px-4 text-offblack border-b border-lightgray gap-4"
                        style={{ gridTemplateColumns: SUBMISSION_HISTORY_GRID }}
                      >
                        <AdminTableHeader>Name</AdminTableHeader>
                        <AdminTableHeader>Submitted By</AdminTableHeader>
                        <AdminTableHeader>Submitted On</AdminTableHeader>
                        <AdminTableHeader>Status</AdminTableHeader>
                        <div className="flex justify-center">
                          <AdminTableHeader>Action</AdminTableHeader>
                        </div>
                      </div>
                      {(archiveItem.submissionHistory || []).map((row) => (
                        <div
                          key={`${row.name}-${row.submittedOn}`}
                          className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4 items-center"
                          style={{ gridTemplateColumns: SUBMISSION_HISTORY_GRID }}
                        >
                          <AdminTableBody className="truncate">{row.name}</AdminTableBody>
                          <AdminTableBody className="truncate">{row.submittedBy}</AdminTableBody>
                          <AdminTableMeta>{formatDateTime(row.submittedOn)}</AdminTableMeta>
                          <AdminTableBody className="truncate">{row.status}</AdminTableBody>
                          <div className="flex justify-center">
                            <AdminLinkAction
                              type="button"
                              onClick={() => alert(`View submission: ${row.name}`)}
                            >
                              View
                            </AdminLinkAction>
                          </div>
                        </div>
                      ))}
                    </DashboardMainContainer>
                  </div>
                </div>
              )}

              {activeTab === 'decision' && (
                <div className="space-y-8">
                  <div>
                    <AdminPanelTitle className="mb-4">Case Checklist</AdminPanelTitle>
                    <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                      <div className="space-y-3">
                        {(archiveItem.reviewChecks || []).map((check) => (
                          <label
                            key={check.id || check.label}
                            className="flex items-start gap-2 cursor-not-allowed opacity-60"
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(check.checked)}
                              disabled
                              readOnly
                              className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
                            />
                            <span className="text-offblack text-sm">{check.label}</span>
                          </label>
                        ))}
                      </div>
                    </DashboardMainContainer>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-6">
                    <div className="space-y-4">
                      <DetailField
                        label="Compliance Decision ID"
                        value={archiveItem.complianceDecisionId}
                      />
                      <DetailField
                        label="Final Outcome"
                        value={archiveItem.finalDecision?.outcome}
                      />
                      <DetailField label="Decided By" value={archiveItem.finalDecision?.decidedBy} />
                      <DetailField
                        label="Decided On"
                        value={formatDateTime(archiveItem.finalDecision?.decidedOn)}
                      />
                      <DetailField
                        label="Finalised On"
                        value={formatDateTime(archiveItem.finalisedOn)}
                      />
                    </div>

                    {archiveItem.finalDecision?.outcome === 'Rejected' ? (
                      <div className="space-y-4">
                        <DetailField
                          label="Rejection Basis"
                          value={
                            (archiveItem.finalDecision.rejectionBasis || []).join(', ') || '—'
                          }
                        />
                        <DetailField
                          label="Rejection Notes"
                          value={archiveItem.finalDecision.rejectionNotes}
                        />
                      </div>
                    ) : null}

                    <div className="space-y-4">
                      <DetailField
                        label="Record Result"
                        value={archiveItem.recordOutcome?.action}
                      />
                      <DetailField
                        label="Business Compliance Record ID"
                        value={archiveItem.recordOutcome?.recordId}
                      />
                      <DetailField
                        label="Record Status"
                        value={
                          archiveItem.recordOutcome?.recordStatus === 'No Record'
                            ? '—'
                            : archiveItem.recordOutcome?.recordStatus
                        }
                      />
                    </div>

                    <div className="space-y-4">
                      <DetailField
                        label="Effective From"
                        value={
                          archiveItem.recordOutcome?.effectiveFrom
                            ? formatDate(archiveItem.recordOutcome.effectiveFrom)
                            : null
                        }
                      />
                      <DetailField
                        label="Expiry Date"
                        value={
                          archiveItem.recordOutcome?.expiryDate
                            ? formatDate(archiveItem.recordOutcome.expiryDate)
                            : null
                        }
                      />
                      <DetailField
                        label="Previous Record"
                        value={archiveItem.recordOutcome?.previousRecord}
                      />
                    </div>
                  </div>

                  {(() => {
                    const infoRequests = (
                      archiveItem.informationRequest
                        ? Array.isArray(archiveItem.informationRequest)
                          ? archiveItem.informationRequest
                          : [archiveItem.informationRequest]
                        : archiveItem.informationRequests || []
                    )
                      .slice()
                      .sort(
                        (a, b) =>
                          new Date(b.availableAt || b.sentOn || 0).getTime() -
                          new Date(a.availableAt || a.sentOn || 0).getTime()
                      );
                    if (!infoRequests.length) return null;
                    return (
                      <div>
                        <AdminPanelTitle className="mb-4">Information Requests</AdminPanelTitle>
                        <div className="space-y-6">
                          {infoRequests.map((request, index) => (
                            <div
                              key={`${request.availableAt || request.sentOn || index}-${index}`}
                              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6"
                            >
                              <DetailField
                                label="Information Requested"
                                value={request.content || request.informationRequested}
                              />
                              <DetailField
                                label="Sent On"
                                value={formatDateTime(request.availableAt || request.sentOn)}
                              />
                              <DetailField
                                label="Due Date"
                                value={formatDateTime(request.dueAt || request.dueDate)}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {activeTab === 'notes' && (
                <div>
                  <DashboardMainContainer className={ADMIN_DETAIL_TABLE_CLASS}>
                    <div
                      className="grid py-3 text-offblack border-b border-lightgray px-4 gap-4"
                      style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
                    >
                      <AdminTableHeader>Note</AdminTableHeader>
                      <AdminTableHeader>Added By</AdminTableHeader>
                      <AdminTableHeader>Added On</AdminTableHeader>
                      <div className="flex justify-center">
                        <AdminTableHeader>Actions</AdminTableHeader>
                      </div>
                    </div>
                    {notes.length > 0 ? (
                      notes.map((note) => (
                        <div
                          key={note.id}
                          className="grid py-4 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-4"
                          style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
                        >
                          <AdminTableBody className="whitespace-pre-wrap break-words">
                            {note.text}
                          </AdminTableBody>
                          <AdminTableBody emphasis>{note.addedBy}</AdminTableBody>
                          <AdminTableMeta>{formatDateTime(note.addedOn)}</AdminTableMeta>
                          <div className="flex justify-center">
                            <AdminLinkAction type="button" onClick={() => setViewingNote(note)}>
                              View
                            </AdminLinkAction>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        className="grid py-4 px-4 text-offblack gap-4"
                        style={{ gridTemplateColumns: NOTES_TABLE_GRID }}
                      >
                        <AdminTableBody>—</AdminTableBody>
                        <AdminTableBody>—</AdminTableBody>
                        <AdminTableMeta>—</AdminTableMeta>
                        <AdminTableBody className="text-center">—</AdminTableBody>
                      </div>
                    )}
                  </DashboardMainContainer>
                </div>
              )}

              {activeTab === 'logs' && (
                <div className="min-w-0 max-w-full">
                  <DashboardMainContainer className={`${ADMIN_DETAIL_TABLE_CLASS} overflow-hidden`}>
                    <div className="w-full min-w-0 max-w-full overflow-x-auto">
                      <div
                        className="grid w-full max-w-full min-w-0 py-3 text-offblack border-b border-lightgray px-4 gap-x-2 sm:gap-x-3 gap-y-2"
                        style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
                      >
                        <AdminTableHeader>Date and Time</AdminTableHeader>
                        <AdminTableHeader>Event</AdminTableHeader>
                        <AdminTableHeader>Details</AdminTableHeader>
                        <AdminTableHeader>Related Record</AdminTableHeader>
                      </div>
                      {paginatedAuditRows.length > 0 ? (
                        paginatedAuditRows.map((log, idx) => {
                          const related =
                            log.relatedRecord && log.relatedRecord !== '—' ? log.relatedRecord : null;
                          const relatedPath = related
                            ? relatedRecordPath(
                                related,
                                archiveItem.linkedCases?.previousComplianceCase
                              )
                            : null;
                          return (
                            <div
                              key={`${log.id || log.dateTime}-${log.event}-${idx}`}
                              className="grid w-full max-w-full min-w-0 py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-2 sm:gap-x-3 gap-y-2 items-start"
                              style={{ gridTemplateColumns: AUDIT_LOG_GRID }}
                            >
                              <AdminTableMeta className="break-words">
                                {formatDateTime(log.dateTime)}
                              </AdminTableMeta>
                              <AdminTableBody className="break-words">{log.event || '—'}</AdminTableBody>
                              <AdminTableBody className="break-words whitespace-pre-wrap">
                                {log.details || '—'}
                              </AdminTableBody>
                              {related && relatedPath ? (
                                <AdminLinkAction type="button" onClick={() => navigate(relatedPath)}>
                                  {related}
                                </AdminLinkAction>
                              ) : (
                                <AdminTableBody className="break-words">{related || '—'}</AdminTableBody>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="py-8 text-center">
                          <AdminSupportingText className="text-mediumgray">—</AdminSupportingText>
                        </div>
                      )}
                    </div>
                  </DashboardMainContainer>
                  <ConfigurationListPaginationFooter
                    show={auditLogsPagination.showPagination}
                    rangeStart={auditLogsPagination.rangeStart}
                    rangeEnd={auditLogsPagination.rangeEnd}
                    totalCount={complianceArchiveAuditRows.length}
                    currentPage={auditLogsPagination.effectivePage}
                    totalPages={auditLogsPagination.totalPages}
                    onPrev={() => setAuditLogCurrentPage((prev) => Math.max(1, prev - 1))}
                    onNext={() =>
                      setAuditLogCurrentPage((prev) =>
                        Math.min(auditLogsPagination.totalPages, prev + 1)
                      )
                    }
                    goToPage={setAuditLogCurrentPage}
                  />
                </div>
              )}
            </div>
          </DashboardMainContainer>
        </div>
      </div>

      {viewingNote ? (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setViewingNote(null)}
        >
          <div
            className={`bg-white rounded-xl p-6 ${ACTION_POPUP_CONTENT_WIDTH_CLASS} mx-4 shadow-lg border border-lightgray`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <AdminPanelTitle className="text-offblack">View</AdminPanelTitle>
              <button
                type="button"
                onClick={() => setViewingNote(null)}
                className="text-offblack hover:text-primaryblue shrink-0"
                aria-label="Close"
              >
                <i className="fi fi-rr-cross text-base" />
              </button>
            </div>
            <div className="space-y-3">
              <AdminDetailFieldValue className="whitespace-pre-wrap">
                {viewingNote.text}
              </AdminDetailFieldValue>
              {(viewingNote.additions || []).map((addition, index) => (
                <div key={`${addition.addedOn}-${index}`} className="space-y-1">
                  <AdminSupportingText>
                    {addition.addedBy} · {formatDateTime(addition.addedOn)}
                  </AdminSupportingText>
                  <AdminDetailFieldValue className="whitespace-pre-wrap">
                    {addition.text}
                  </AdminDetailFieldValue>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </AdminLayout>
  );
};

export default ComplianceArchiveDetail;
